import { Pressable, SxProp, View } from 'dripsy';
import React, { useState, useRef, useEffect } from 'react';
import ReactNative, { StyleSheet, LayoutChangeEvent, Animated, PanResponder } from 'react-native';
import { Layout, useSharedValue } from 'react-native-reanimated';
import { TextCaption } from '../typography';

const THUMB_SIZE = 24;

interface SliderProps {
  minValue: number;
  maxValue: number;
  value: number;
  labelFormatter?: (value: number) => string;
  onValueChange: (value: number) => void;
  step: number;
  disabled?: boolean;
  labelSliding?: boolean;
  sx?: SxProp;
  valueSx?: SxProp;
}

export const Slider: React.FC<SliderProps> = ({
  minValue,
  maxValue,
  value,
  labelFormatter,
  onValueChange,
  step,
  disabled,
  labelSliding,
  sx,
  valueSx
}) => {
  const [sliderValue, setSliderValue] = useState(value);
  const [containerWidth, setContainerWidth] = useState(0);
  const [labelWidth, setLabelWidth] = useState(0);

  const [offsetX, setOffsetX] = useState(0);
  const containerRef = useRef<ReactNative.View>(null);
  const thumbRef = useRef<ReactNative.View>(null);
  const thumbX = useSharedValue(0);

  useEffect(() => {
    const thumbXValue = value * ((containerWidth - THUMB_SIZE) / maxValue);
    thumbX.value = thumbXValue;
    setSliderValue(value);
  }, [value, containerWidth]);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.measure((x, y, width, height, pageX, pageY) => {
        setOffsetX(pageX);
      });
    }
  }, [containerRef.current]);

  const getLabelValue = (originValue: number) => {
    const labelValue = originValue + THUMB_SIZE / 2 - labelWidth / 2;
    if (labelValue < 0) {
      return 0;
    } else if (labelValue + labelWidth > containerWidth) {
      return containerWidth - labelWidth;
    }
    return labelValue;
  };

  const handleContainerLayout = (event: LayoutChangeEvent) => {
    setContainerWidth(event.nativeEvent.layout.width);
  };

  const handleValueLayout = (event: LayoutChangeEvent) => {
    setLabelWidth(event.nativeEvent.layout.width);
  };

  const onTap = (percentage: number) => {
    const newValue = (percentage / 100) * maxValue;
    var newValueRounded = Math.round(newValue / step) * step;
    if (newValueRounded < minValue) {
      newValueRounded = minValue;
    } else if (newValueRounded > maxValue) {
      newValueRounded = maxValue;
    }
    const thumbXValue = newValueRounded * ((containerWidth - THUMB_SIZE) / maxValue);
    thumbX.value = thumbXValue;
    setSliderValue(newValueRounded);
    onValueChange(newValueRounded);
  };

  const panResponder = PanResponder.create({
    onMoveShouldSetPanResponder: (evt, gestureState) => {
      if (disabled) return false;
      return Math.abs(gestureState.dx) > 5 || Math.abs(gestureState.dy) > 5;
    },
    onPanResponderMove: (evt, gestureState) => {
      if (disabled) return;
      const touchX = gestureState.moveX / 0.9;
      const position = touchX - offsetX;
      const percentage = (position / containerWidth) * 100;
      onTap(percentage);
    }
  });

  return (
    <View sx={{ ...styles.container, ...sx }} ref={containerRef} {...panResponder.panHandlers} onLayout={handleContainerLayout}>
      <Pressable
        sx={styles.track}
        onPress={(event) => {
          if (disabled) return;
          const touchX = event.nativeEvent.pageX / 0.9;
          const position = touchX - offsetX;
          const percentage = (position / containerWidth) * 100;
          onTap(percentage);
        }}>
        <Animated.View
          style={[
            {
              position: 'absolute',
              top: 0,
              left: 0,
              height: '100%',
              backgroundColor: disabled ? '#DCDCDC' : '#305992',
              borderRadius: 5
            },
            {
              width: thumbX.value
            }
          ]}
        />
      </Pressable>
      <View sx={styles.thumbWrapper}>
        <Animated.View style={[cssStyles.thumb, { transform: [{ translateX: thumbX.value }] }]} ref={thumbRef}>
          <View sx={styles.thumbInner} />
        </Animated.View>
      </View>
      <View
        sx={{ ...styles.valueContainer, ...valueSx }}
        style={labelSliding && { transform: [{ translateX: getLabelValue(thumbX.value) }] }}>
        <TextCaption onLayout={handleValueLayout}>{labelFormatter ? labelFormatter(sliderValue) : sliderValue}</TextCaption>
      </View>
    </View>
  );
};

enum StyleName {
  container = 'container',
  track = 'track',
  thumbWrapper = 'thumbWrapper',
  thumbInner = 'thumbInner',
  valueContainer = 'valueContainer'
}

const cssStyles = StyleSheet.create({
  thumb: {
    position: 'absolute',
    top: -18,
    left: 0
  }
});

const styles: Record<StyleName, SxProp> = {
  container: {
    width: '100%',
    alignItems: 'stretch',
    justifyContent: 'center'
  },
  track: {
    height: 6,
    backgroundColor: '#DCDCDC',
    borderRadius: 5,
    marginTop: 15
  },
  thumbWrapper: {
    position: 'absolute',
    top: '$6'
  },
  thumbInner: {
    width: '$6',
    height: '$6',
    borderRadius: 100,
    backgroundColor: '#FFF',
    borderWidth: 1,
    borderColor: '#AFAFAF',
    shadowColor: '#000000',
    shadowOffset: {
      width: 0,
      height: 2
    },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 5,
    zIndex: 10
  },
  valueContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    marginTop: 15
  }
};
